import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Context } from 'Context/Context';
import { casesStatusUpdateApi, firmOverviewApi, raiseNotificationApi } from 'Constants';
import { getCaseType } from 'Utils';
import useIsMobile from 'Hooks/useIsMobile';
import { verificationStatusLine, capWords } from 'Modals/UserNotVerifiedModal';
import { estimateOf, ICON } from './CaseCard';
import { CardSkeleton, Svg, C, Tip } from './CaseListUI';

// ---------------------------------------------------------------------------
// Firm overview (design: "Firm Overview Mockup") for law firms without Premium.
//   mode "unverified": profile not verified yet - status note on top, every
//                      "Request to view more" locked until our admin verifies it
//   mode "free":       verified firms (with or without Premium) - saved cases
//                      can be requested; recommended ones too when the server
//                      says canRequestRecommended (always for Premium), else
//                      they're visible but locked behind Premium
// Cases the firm already requested stay listed with their status (myStatus).
// Data: /case/firmOverview { stats, platform, saved, recommended, canRequestRecommended }.
// `preloaded` skips the fetch (the unverified page loads it first).
// ---------------------------------------------------------------------------

const SPARKLE = 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z';
const STAR = 'M12 3l2.4 5 5.6.8-4 3.9.9 5.5L12 15.6 7.1 18.2l.9-5.5-4-3.9 5.6-.8z';
const GLOBE = ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z'];
const LINES = 'M4 6h16M4 12h16M4 18h10';
const CROWN = 'M3 7l4.5 4L12 5l4.5 6L21 7l-2 11H5z';

const LockIcon = ({ size = 13 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" className="flex-none">
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);
const CrownIcon = ({ size = 14, className = '' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={`flex-none ${className}`}><path d={CROWN} /></svg>
);

// why the button is locked for a firm still under review (2) / not approved (5) / profile not finished
export const lockedReason = (verified) => verified === 5
  ? "Your profile wasn't approved. Update your details and resubmit. Once it's verified, you can request to view more."
  : verified === 2
    ? 'Your profile is under review. Once our admin verifies it, you can request to view more about this case.'
    : 'Complete your profile first. Once our admin verifies it, you can request to view more.';
const PREMIUM_REASON = 'Recommended cases open with Premium. Upgrade to request to view more about this case.';

function StatusNote({ verified }) {
  const isMobile = useIsMobile();
  const s = verificationStatusLine(verified);
  const StatusIcon = s.icon;
  return (
    <section className={`flex rounded-md border ${s.tone} ${isMobile ? 'flex-col items-stretch gap-3 p-3.5' : 'items-center justify-between gap-6 py-4 px-5'}`}>
      <div className="flex items-start gap-3 min-w-0">
        <StatusIcon size={18} strokeWidth={2} className={`flex-none mt-[2px] ${s.iconTone}`} />
        <p className="m-0 font-plexsans text-[14.5px] leading-[1.55] text-navy">
          <span className="font-semibold">{s.bold}</span> <span className="text-navy/[.72]">{s.rest}</span>
        </p>
      </div>
      <Link to={s.to} className={`flex-none inline-flex items-center justify-center h-11 px-5 rounded-md font-plexsans font-semibold text-[13.5px] leading-none text-ivory bg-brass hover:bg-brassDark no-underline ${isMobile ? 'w-full' : ''}`}>
        {s.button}
      </Link>
    </section>
  );
}

function StatCard({ icon, value, label, empty }) {
  const isMobile = useIsMobile();
  const total = value?.total || 0;
  const month = value?.month || 0;
  return (
    <div className={`bg-white border border-navy/[.12] rounded-lg flex flex-col ${isMobile ? 'p-3.5 gap-1.5' : 'py-5 px-[22px] gap-2.5'}`}>
      {!isMobile &&
        <span className="w-9 h-9 rounded-full bg-brass/[.1] flex items-center justify-center">
          <Svg d={icon} size={18} width={2} stroke={C.brass} />
        </span>}
      <span className={`font-spectral leading-none text-navy ${isMobile ? 'text-[32px]' : 'text-[40px]'}`}>{total}</span>
      <span className={`font-plexsans font-medium leading-[1.35] text-navy ${isMobile ? 'text-[13px]' : 'text-[14px]'}`}>{label}</span>
      {month > 0
        ? <span className="font-plexsans font-semibold text-[12.5px] leading-none text-brass">{month} this month</span>
        : total === 0 && <span className="font-plexsans text-[12px] leading-[1.4] text-navy/[.6]">{empty}</span>}
    </div>
  );
}

function Tag({ saved }) {
  return saved ? (
    <span className="inline-flex items-center gap-1.5 font-plexsans font-semibold text-[12px] py-[6px] px-2.5 leading-none text-brass bg-white border border-brass/[.4] rounded-[20px] whitespace-nowrap">
      <Svg d={ICON.bookmark} size={12} width={2.2} stroke={C.brass} />Client saved your firm
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 font-plexsans font-semibold text-[12px] py-[6px] px-2.5 leading-none text-navy bg-white border border-navy/[.22] rounded-[20px] whitespace-nowrap">
      <Svg d={SPARKLE} size={12} width={2.2} stroke={C.navy} />AI matched your firm
    </span>
  );
}

// this firm's status on a case it already requested (myStatus from the server)
const STATUS = {
  requested: { label: 'Requested · waiting for the client', tone: 'muted' },
  approved: { label: 'Approved by the client', tone: 'green', open: true },
  noConflict: { label: 'In progress', tone: 'green', open: true },
  conflict: { label: 'Marked as a conflict', tone: 'muted' },
  reqToClose: { label: 'Closing', tone: 'muted' },
  closed: { label: 'Closed', tone: 'muted' },
};

// one case: the short summary + its button
// button: { kind: 'request' | 'status' | 'locked' | 'premium', reason, onClick, status, to }
function CaseRow({ c, saved, button, last }) {
  const isMobile = useIsMobile();
  const place = [capWords(c.caseCity), c.caseState].filter(Boolean).join(', ');
  const est = estimateOf(c);
  const meta = [place, c.created_at ? 'Posted ' + moment(c.created_at).format('MMM D, YYYY') : null].filter(Boolean).join(' · ');
  const base = `inline-flex items-center justify-center gap-2 rounded-md font-plexsans font-semibold text-[13.5px] leading-none whitespace-nowrap ${isMobile ? 'w-full h-11' : 'py-[13px] px-5'}`;

  let btn;
  if (button.kind === 'request') {
    btn = <button type="button" onClick={button.onClick} disabled={button.busy}
      className={`${base} border-0 text-ivory bg-brass hover:bg-brassDark ${button.busy ? 'opacity-70 cursor-wait' : 'cursor-pointer'}`}>
      {button.busy ? 'Sending…' : 'Request to view more →'}
    </button>;
  } else if (button.kind === 'status') {
    const st = STATUS[button.status] || STATUS.requested;
    const pill = (
      <span className={`inline-flex items-center gap-1.5 font-plexsans font-semibold text-[12.5px] leading-none py-2 px-3 rounded-[20px] whitespace-nowrap border ${st.tone === 'green' ? 'text-success bg-success/[.07] border-success/[.3]' : 'text-navy/[.65] bg-navy/[.05] border-navy/[.12]'}`}>
        {st.tone === 'green'
          ? <Svg d="M5 12l4 4 10-10" size={12} width={2.4} stroke="currentColor" />
          : <Svg d={['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2']} size={12} width={2.2} stroke="currentColor" />}
        {st.label}
      </span>
    );
    // "Requested" opens the Requested cases list
    if (button.status === 'requested' && button.onRequested) {
      btn = (
        <button type="button" onClick={button.onRequested} title="See it in Requested cases"
          className={`inline-flex items-center gap-1.5 font-plexsans font-semibold text-[12.5px] leading-none py-2 px-3 rounded-[20px] whitespace-nowrap border cursor-pointer text-navy/[.65] bg-navy/[.05] border-navy/[.12] hover:bg-navy/[.09] hover:text-navy ${isMobile ? 'justify-center h-11' : ''}`}>
          <Svg d={['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2']} size={12} width={2.2} stroke="currentColor" />
          {st.label}
          <span aria-hidden="true">→</span>
        </button>
      );
    } else btn = st.open ? (
      <div className={`flex ${isMobile ? 'flex-col items-stretch gap-2' : 'items-center gap-3'}`}>
        {pill}
        <Link to={button.to} className={`${base} no-underline text-brass bg-white border border-brass/[.45] hover:bg-brass/[.06]`}>Open case →</Link>
      </div>
    ) : pill;
  } else {
    const premium = button.kind === 'premium';
    const el = (
      <button type="button" aria-label={`${premium ? 'Request with Premium' : 'Request to view more'} - locked. ${button.reason}`}
        onClick={button.onClick}
        className={`${base} ${premium
          ? 'text-brass bg-brass/[.08] border border-dashed border-brass/[.5] cursor-pointer hover:bg-brass/[.12]'
          : 'text-navy/[.62] bg-navy/[.06] border border-navy/[.14] cursor-not-allowed'}`}>
        <LockIcon />{premium ? 'Request with Premium' : 'Request to view more'}
      </button>
    );
    btn = isMobile ? el : <Tip text={button.reason} align="right" width={270}>{el}</Tip>;
  }

  if (isMobile) {
    return (
      <div className="bg-white border border-navy/[.12] rounded-lg p-4 flex flex-col gap-2.5">
        <div><Tag saved={saved} /></div>
        <div className="flex flex-col gap-1">
          <span className="font-plexsans font-semibold text-[17px] leading-[1.3] text-navy">{getCaseType(c.caseType) || 'Case'}</span>
          {meta && <span className="font-plexsans text-[13.5px] leading-[1.5] text-navy/[.65]">{meta}</span>}
          {est && <span className="font-plexsans font-semibold text-[13.5px] leading-[1.5] text-brass">Est. {est}</span>}
        </div>
        {btn}
      </div>
    );
  }
  return (
    <div className={`flex items-center justify-between flex-wrap gap-x-6 gap-y-4 py-[22px] px-7 ${last ? '' : 'border-b border-navy/[.1]'}`}>
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-plexsans font-semibold text-[20px] leading-[1.3] text-navy">{getCaseType(c.caseType) || 'Case'}</span>
          <Tag saved={saved} />
        </div>
        {meta && <span className="font-plexsans text-[15px] leading-[1.5] text-navy/[.65]">{meta}</span>}
        {est && <span className="font-plexsans font-semibold text-[15px] leading-[1.5] text-brass">Est. {est}</span>}
      </div>
      <div className="flex-none">{btn}</div>
    </div>
  );
}

function CaseSection({ title, hint, count, emptyText, children, band }) {
  const isMobile = useIsMobile();
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="m-0 font-plexsans font-semibold text-[13px] leading-none tracking-[.08em] uppercase text-navy/[.7]">{title} · {count}</h3>
        {hint && !isMobile && <span className="font-plexsans text-[13px] text-navy/[.62]">{hint}</span>}
      </div>
      {band}
      {count === 0
        ? <div className="bg-white border border-navy/[.12] rounded-md py-5 px-6 font-plexsans text-[14px] leading-[1.5] text-navy/[.62]">{emptyText}</div>
        : <div className={isMobile ? 'flex flex-col gap-3' : 'bg-white border border-navy/[.12] rounded-md'}>{children}</div>}
    </section>
  );
}

// onShowRequested: switch to the Requested cases list (the case lists page passes it)
function FirmOverview({ mode = 'free', verifiedState = null, preloaded = null, onShowRequested }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { makeApiCallFunc, userDetails } = useContext(Context);
  const [data, setData] = useState(preloaded);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [requested, setRequested] = useState([]);

  useEffect(() => {
    if (preloaded) { setData(preloaded); return; }
    makeApiCallFunc(firmOverviewApi, {}, ({ data }) => setData(data || {}), () => setFailed(true), null, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloaded]);

  if (failed) {
    return <div className="bg-white border border-navy/[.12] rounded-md py-10 px-6 text-center font-plexsans text-[14px] text-navy/[.62]">Could not load your firm overview. Please refresh the page.</div>;
  }
  if (!data) return <CardSkeleton count={2} />;

  const unverified = mode === 'unverified';
  const stats = data.stats || {};
  const platform = data.platform || {};
  const saved = data.saved || [];
  const recommended = data.recommended || [];
  const canRequestRecommended = !unverified && !!data.canRequestRecommended;
  const assistant = !!userDetails?.isUserAssistant;

  const notice = (msg) => toast.info(msg, { toastId: 'overview-locked' });
  const goPremium = () => {
    if (assistant) { notice('The firm account owner can upgrade to Premium.'); return; }
    navigate('/attorney/subscription?from=overview');
  };

  // same request + client notification as the case lists
  const request = (c) => {
    if (busyId) return;
    setBusyId(c._id);
    makeApiCallFunc(casesStatusUpdateApi,
      { caseId: c._id, userId: userDetails?._id, assistantId: null, caseStatus: 'requested' },
      () => {
        setBusyId(null);
        setRequested((prev) => [...prev, c._id]);
        toast.success("Request sent. You'll find it under Requested cases.");
        makeApiCallFunc(raiseNotificationApi, {
          title: `${userDetails?.firstName} ${userDetails?.lastName} has sent a new request for your case - ${getCaseType(c.caseType)}`,
          is_read: false,
          sender: 'professional',
          type: '3',
          notificationCategory: 1,
          caseNumber: c.caseNumber,
          receiver: c.owner,
          additionalInfo: { caseId: c._id, userId: userDetails?._id },
          content: `The Law firm case manager ${userDetails?.firstName} ${userDetails?.lastName} has requested to view your case details.  Please review the Law firm's details and approve or deny their request.`,
        }, () => {}, (e) => console.log(e), null, true);
      },
      (err) => {
        setBusyId(null);
        toast.error(err?.response?.data?.message || 'Could not send your request. Please try again.');
      },
      null, true);
  };

  const buttonFor = (c, isSaved) => {
    // already requested (just now, or earlier): show where it stands instead of a button
    const status = requested.includes(c._id) ? 'requested' : c.myStatus;
    if (status && status !== 'denied') return { kind: 'status', status, to: `/attorney/caseDetails/${c._id}`, onRequested: onShowRequested };
    if (unverified) {
      const reason = lockedReason(verifiedState);
      return { kind: 'locked', reason, onClick: () => { if (isMobile) notice(reason); } };
    }
    if (isSaved || canRequestRecommended) return { kind: 'request', busy: busyId === c._id, onClick: () => request(c) };
    return { kind: 'premium', reason: PREMIUM_REASON, onClick: goPremium };
  };

  const premiumBand = !unverified && !canRequestRecommended && recommended.length > 0 ? (
    <div className={`flex rounded-md bg-brass/[.06] border border-brass/[.28] ${isMobile ? 'flex-col items-stretch gap-3 p-3.5' : 'items-center justify-between gap-6 py-4 px-5'}`}>
      <div className="flex items-start gap-2.5 font-plexsans text-[14px] leading-[1.5] text-navy">
        <CrownIcon size={16} className="text-brass mt-[3px]" />
        <span><span className="font-semibold">{recommended.length === 1 ? '1 more case matched your firm.' : `${recommended.length} more cases matched your firm.`}</span> You can see them now and request to view more with Premium.</span>
      </div>
      <button type="button" onClick={goPremium}
        className={`flex-none inline-flex items-center justify-center gap-[7px] py-3 px-[18px] rounded-md border-0 bg-navy hover:bg-[#0d1528] text-ivory font-plexsans font-semibold text-[13.5px] leading-none cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2 ${isMobile ? 'w-full h-11' : ''}`}>
        <CrownIcon className="text-gold" />Upgrade to Premium
      </button>
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-[26px] font-plexsans">
      {unverified && <StatusNote verified={verifiedState} />}

      <section className="flex flex-col gap-3.5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className={`m-0 font-spectral font-medium text-navy ${isMobile ? 'text-[22px]' : 'text-[26px]'}`}>Your firm on Quick Case Review</h2>
          {!isMobile && <span className="text-[13px] text-navy/[.62]">All-time totals · this month underneath</span>}
        </div>
        <div className={`grid gap-3 lg:gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-[repeat(auto-fit,minmax(200px,1fr))]'}`}>
          <StatCard icon={STAR} value={stats.recommended} label="Times recommended to clients" empty="Clients will see your firm as new cases come in." />
          <StatCard icon={ICON.bookmark} value={stats.saved} label="Clients saved your firm" empty="When a client saves your firm, it shows here." />
          <StatCard icon={GLOBE} value={stats.websiteVisits} label="Website visits from client matches" empty="Clicks to your website from client matches show here." />
          <StatCard icon={LINES} value={stats.casesMatched} label="Cases matched to your firm" empty="Our AI matches firms to every new case." />
        </div>
      </section>

      <section aria-label="On the platform" className={`flex bg-white border border-navy/[.12] rounded-lg ${isMobile ? 'flex-col gap-2 p-3.5' : 'items-center flex-wrap gap-x-8 gap-y-3 py-4 px-[22px]'}`}>
        <span className="text-[11px] font-semibold tracking-[.1em] uppercase text-brass">On the platform</span>
        <span className="text-[14.5px] text-navy/[.72]"><strong className="font-bold text-navy">{platform.openCases || 0}</strong> open cases in Texas</span>
        <span className="text-[14.5px] text-navy/[.72]"><strong className="font-bold text-navy">{platform.postedThisMonth || 0}</strong> posted this month</span>
        {platform.sos > 0 && <span className="text-[14.5px] text-navy/[.72]"><strong className="font-bold text-navy">{platform.sos}</strong> SOS {platform.sos === 1 ? 'case needs' : 'cases need'} a lawyer now</span>}
      </section>

      <CaseSection title="Saved your firm" count={saved.length}
        emptyText="No client has saved your firm yet. When one does, the case shows here.">
        {saved.map((c, i) => <CaseRow key={c._id} c={c} saved button={buttonFor(c, true)} last={i === saved.length - 1} />)}
      </CaseSection>

      <CaseSection title="Recommended for your firm" count={recommended.length}
        hint="Cases where our AI matched your firm to the client" band={premiumBand}
        emptyText="No AI matches yet. As new cases come in, the ones that fit your firm show here.">
        {recommended.map((c, i) => <CaseRow key={c._id} c={c} saved={false} button={buttonFor(c, false)} last={i === recommended.length - 1} />)}
      </CaseSection>
    </div>
  );
}

export default FirmOverview;
